import { useCallback, useMemo, useEffect } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import TileCard from 'tripPlanner/components/EntryPoints/Common/Tile/TileCard'
import LineBedSofaIcon from 'LuxKit/Icons/line/LineBedSofaIcon'
import LineBalanceScaleIcon from 'LuxKit/Icons/line/LineBalanceScaleIcon'
import LineSignAltIcon from 'LuxKit/Icons/line/LineSignAltIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import { arrayToMap } from 'lib/array/arrayUtils'
import { trackClientEvent } from 'analytics/analytics'

interface Props {
  hotelCity: string
  tripPaces: Array<{
    tripPace: string
    selected: boolean
  }>
  onPaceSelect: (pace: string) => void
  onNext: () => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripPaceSelection({
  hotelCity,
  tripPaces,
  onPaceSelect,
  onNext,
  isGeneratingTrip,
  stepNumber,
  totalSteps,
}: Props) {
  const intl = useIntl()
  const handleOnClick = useCallback((pace: string) => {
    onPaceSelect(pace)
    onNext()
  }, [onPaceSelect, onNext])

  const tripPaceMap = useMemo(() =>
    arrayToMap(tripPaces, pace => pace.tripPace, pace => pace.selected),
  [tripPaces])

  useEffect(() => {
    trackClientEvent({
      subject: 'trip_pace',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const TRIP_PACES = [
    {
      tripPace: 'Relaxed',
      title: intl.formatMessage({
        id: 'IlXOHKejrOOiNYYU4tcT',
        defaultMessage: 'Relaxed',
        description: 'Title for relaxed trip pace option in the trip planner feature',
      }),
      description: intl.formatMessage({
        id: 'aqJhqm6_NHziVRIxM1ln',
        defaultMessage: 'Take your time, enjoy the vibe.',
        description: 'Description for relaxed trip pace option in the trip planner',
      }),
      icon: <LineBedSofaIcon />,
    },
    {
      tripPace: 'Balanced',
      title: intl.formatMessage({
        id: 'JVlCnnnfoPhSvXGWJdqU',
        defaultMessage: 'Explore a little',
        description: 'Title for balanced trip pace option in the trip planner feature',
      }),
      description: intl.formatMessage({
        id: 'UivI2jL3Vlfro0jCJ7CC',
        defaultMessage: 'A mix of activities and relaxation.',
        description: 'Description for balanced trip pace option in the trip planner',
      }),
      icon: <LineBalanceScaleIcon />,
    },
    {
      tripPace: 'Active',
      title: intl.formatMessage({
        id: 'xVG8N655Hb_TDfu9Nx4E',
        defaultMessage: 'Active',
        description: 'Title for active trip pace option in the trip planner feature',
      }),
      description: intl.formatMessage({
        id: 'ZniR60qC-MkW3DJX78ia',
        defaultMessage: 'Pack in as much as possible.',
        description: 'Description for active trip pace option in the trip planner feature',
      }),
      icon: <LineSignAltIcon />,
    },
  ]

  const stepText = intl.formatMessage(
    {
      id: 'ewX_UMLdx4cN8zd8qwrw',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber, totalSteps },
  )

  const headingText = intl.formatMessage(
    {
      id: 'Fpx5YzcD3TnWHRla1rKt',
      defaultMessage: 'Trip <i>Pace</i>',
      description: 'Heading text with italicized Pace in the trip planner feature',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const descriptionText = intl.formatMessage(
    {
      id: 'IPPNg9k4oTpmji3Qc6d9',
      defaultMessage: 'What kind of tempo are you after for your {hotelCity} trip?',
      description: 'Question asking about preferred trip tempo on the hotel page',
    },
    { hotelCity },
  )

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepText}</BodyText>
        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {descriptionText}
        </BodyText>
      </VerticalSpacer>

      <TileCardsGroup>
        {TRIP_PACES.map(pace => (
          <TileCard
            key={pace.tripPace}
            title={pace.title}
            subtitle={pace.description}
            icon={pace.icon}
            value={pace.tripPace}
            onClick={handleOnClick}
            selected={!!tripPaceMap.get(pace.tripPace)}
            disabled={isGeneratingTrip}
          />))}
      </TileCardsGroup>
    </Group>
  )
}

export default AITripPaceSelection
